
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, Database, RotateCw, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { EditWebsiteCredentialsDialog } from "@/components/wordpress/EditWebsiteCredentialsDialog";

interface WebsiteCardProps {
  title: string;
  url: string;
  platform: "wordpress" | "shopify" | "woocommerce";
  lastBackup: string;
  nextBackup: string;
  storageUsed: number;
  status: "active" | "paused";
  isLoading?: boolean;
  onBackupNow: () => void;
  onViewBackups: () => void;
  onRestore: () => void;
  websiteId: string;
}

const WebsiteCard: React.FC<WebsiteCardProps> = ({
  title,
  url,
  platform,
  lastBackup,
  nextBackup,
  storageUsed,
  status,
  isLoading = false,
  onBackupNow,
  onViewBackups,
  onRestore,
  websiteId,
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Platform-specific icon and color
  const getPlatformDetails = () => {
    switch (platform) {
      case "wordpress":
        return { icon: "W", color: "bg-blue-100 text-blue-800" };
      case "shopify":
        return { icon: "S", color: "bg-green-100 text-green-800" };
      case "woocommerce":
        return { icon: "WC", color: "bg-purple-100 text-purple-800" };
    }
  };

  const platformDetails = getPlatformDetails();

  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center p-6">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${platformDetails.color} mr-4`}
            >
              <span className="font-medium">{platformDetails.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium truncate">{title}</h3>
              <p className="text-sm text-gray-500 truncate">{url}</p>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                }`}
              >
                {status === "active" ? "Active" : "Paused"}
              </div>
              {platform === "wordpress" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditDialogOpen(true)}
                  className="p-1 h-8 w-8"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 divide-x divide-gray-200 border-y border-gray-200">
            <div className="p-4 text-center">
              <div className="text-2xl font-semibold">{lastBackup}</div>
              <div className="text-xs text-gray-500 mt-1">Last Backup</div>
            </div>
            <div className="p-4 text-center">
              <div className="text-2xl font-semibold">{nextBackup}</div>
              <div className="text-xs text-gray-500 mt-1">Next Backup</div>
            </div>
          </div>

          {/* Storage */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">Storage Used</span>
              <span className="text-sm text-gray-500">{storageUsed}%</span>
            </div>
            <Progress value={storageUsed} className="h-2" />
          </div>
        </CardContent>

        <CardFooter className="flex justify-between p-4 pt-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={onBackupNow}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Backing up...
              </>
            ) : (
              <>
                <Database className="mr-1 h-4 w-4" />
                Backup Now
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            asChild
            className="flex-1"
            onClick={onViewBackups}
          >
            <Link to={`/backup-history/${websiteId}`}>
              View Backups
            </Link>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onRestore}
            className="flex-1"
          >
            <RotateCw className="mr-1 h-4 w-4" />
            Restore
          </Button>
        </CardFooter>
      </Card>

      {/* Edit Credentials Dialog */}
      <EditWebsiteCredentialsDialog
        websiteId={websiteId}
        websiteTitle={title}
        websiteUrl={url}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />
    </>
  );
};

export default WebsiteCard;
